import { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { HelpCircle } from "lucide-react";
import { Input } from "@/components/atoms/studio/ui/input";
import { Button } from "@/components/atoms/studio/ui/button";
import { SearchableSelect } from "@/components/organisms/studio/SearchableSelect";
import { useDropdown } from "@/hooks/studio-platform";
import { useRunStore } from "@/stores/studio/runStore";
import type { SearchableOption, SkillInput, SkillInputSelect } from "@/services/studio/types";

interface SkillFormProps {
  skillId: string;
  skillTitle?: string;
  inputs: SkillInput[];
  onSubmitted?: (values: Record<string, unknown>) => void;
  /** Pre-filled values keyed by input name (rendered as strings in the form). */
  initialValues?: Record<string, unknown>;
}

function isSelect(input: SkillInput): input is SkillInputSelect {
  return input.type === "select";
}

/** Sensible default help text when a SKILL.md hasn't declared one. */
function defaultHelpFor(input: SkillInput): string | undefined {
  if (isSelect(input)) {
    if (input.options && input.options.length > 0) {
      return `Pick one of ${input.options.length} option(s). Type to filter.`;
    }
    if (input.source) {
      const friendly: Record<string, string> = {
        list_objects: "Pick a Salesforce object — Custom (__c) or Standard.",
        list_classes: "Pick an Apex class from this org.",
        list_flows: "Pick a Flow.",
        list_perm_sets: "Pick a Permission Set.",
        list_users: "Pick a user.",
        list_metadata: "Pick a metadata item from this org.",
        get_object_fields: "Pick a field — list reflects the object you selected above.",
      };
      return friendly[input.source] ?? `Search the list (loaded via ${input.source}).`;
    }
    return "Pick an option.";
  }
  if (input.type === "number") return "Numeric value.";
  return undefined;
}

interface DropdownFieldProps {
  input: SkillInputSelect;
  value: string;
  onChange: (v: string) => void;
  onResolvedOption?: (option: SearchableOption | null) => void;
  formValues: Record<string, string>;
}

/** Replace `{{name}}` tokens in arg values with the live form value for `name`. */
function interpolateArgs(
  raw: Record<string, unknown> | undefined,
  formValues: Record<string, string>
): Record<string, string> {
  if (!raw) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v !== "string") {
      out[k] = String(v);
      continue;
    }
    out[k] = v.replace(/\{\{\s*([a-zA-Z_][\w.]*)\s*\}\}/g, (_, name: string) => {
      const top = name.split(".")[0];
      return formValues[top] ?? "";
    });
  }
  return out;
}

function hasUnresolvedTokens(args: Record<string, string>): boolean {
  return Object.values(args).some((v) => v === "" || /\{\{[^}]+\}\}/.test(v));
}

function DropdownField({ input, value, onChange, onResolvedOption, formValues }: DropdownFieldProps) {
  const args = useMemo(
    () => interpolateArgs(input.source_args ?? input.args, formValues),
    [input.source_args, input.args, formValues]
  );
  const blocked = hasUnresolvedTokens(args);
  const { data, isLoading, isError, error } = useDropdown(input.source, args, !blocked);
  const allowed = useMemo(() => new Set(input.allowed_values ?? []), [input.allowed_values]);
  const staticOptions = input.options && input.options.length > 0
    ? input.options
    : null;
  const opts = allowed.size
    ? (staticOptions ?? data?.options ?? []).filter((option) => allowed.has(option.value))
    : staticOptions ?? data?.options ?? [];

  useEffect(() => {
    if (blocked && value) {
      onChange("");
      onResolvedOption?.(null);
      return;
    }
    if (!blocked && !isLoading && value && opts.length > 0 && !opts.some((option) => option.value === value)) {
      onChange("");
      onResolvedOption?.(null);
      return;
    }
    if (!blocked && value) {
      onResolvedOption?.(opts.find((option) => option.value === value) ?? null);
    }
  }, [blocked, isLoading, onChange, onResolvedOption, opts, value]);

  const selectValue = (next: string) => {
    const option = opts.find((item) => item.value === next) ?? null;
    onResolvedOption?.(option);
    onChange(next);
  };

  if (blocked) {
    return (
      <div className="text-xs text-muted-foreground font-mono px-3 py-2 border border-dashed border-input rounded-md bg-muted/30">
        Select the upstream input first to populate this list.
      </div>
    );
  }

  if (staticOptions) {
    return (
      <SearchableSelect
        value={value}
        onChange={selectValue}
        options={opts}
        placeholder={input.placeholder ?? "Select…"}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="text-xs text-muted-foreground font-mono px-3 py-2 border border-input rounded-md bg-muted/40">
        Loading options…
      </div>
    );
  }

  if (isError) {
    const msg = error instanceof Error ? error.message : "request_failed";
    return (
      <div className="text-xs text-destructive font-mono px-3 py-2 border border-destructive/40 rounded-md bg-destructive/5">
        Could not load options ({msg}). Check the selected org.
      </div>
    );
  }

  return (
    <SearchableSelect
      value={value}
      onChange={selectValue}
      options={opts}
      placeholder={input.placeholder ?? "Select…"}
      emptyHint="No options match this filter."
    />
  );
}

export function SkillForm({
  skillId,
  skillTitle,
  inputs,
  onSubmitted,
  initialValues,
}: SkillFormProps) {
  const start = useRunStore(skillId, (s) => s.start);
  const status = useRunStore(skillId, (s) => s.status);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, SearchableOption>>({});

  const defaultValues = useMemo(() => {
    const out: Record<string, string> = {};
    for (const inp of inputs) {
      const override = initialValues?.[inp.name];
      if (override !== undefined && override !== null) {
        out[inp.name] = String(override);
        continue;
      }
      const def = inp.default;
      out[inp.name] = def !== undefined && def !== null ? String(def) : "";
    }
    return out;
  }, [inputs, initialValues]);

  const { control, register, handleSubmit, reset, watch, formState } = useForm<
    Record<string, string>
  >({
    defaultValues,
  });
  const formValues = watch();

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = handleSubmit((values) => {
    const payload: Record<string, unknown> = { run_mode: "fast" };
    for (const inp of inputs) {
      const raw = values[inp.name];
      if (raw === undefined || raw === "") continue;
      payload[inp.name] =
        inp.type === "number"
          ? Number(raw)
          : inp.type === "boolean"
            ? raw === "true" || raw === "on"
          : raw;
      if (isSelect(inp)) {
        const selected = selectedOptions[inp.name];
        enrichSelectedMetadata(payload, inp, raw, selected);
      }
    }
    start(skillId, payload, skillTitle);
    onSubmitted?.(payload);
  });

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {inputs.length === 0 ? (
        <p className="text-sm text-muted-foreground font-mono">
        This migration can run without any extra information.
        </p>
      ) : null}

      {inputs.map((input) => {
        const required = input.required ?? false;
        const label = input.label ?? input.name;
        const help = input.help ?? defaultHelpFor(input);
        return (
          <div key={input.name} className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <label className="block text-xs font-mono text-muted-foreground">
                {label}
                {required ? " *" : ""}
              </label>
              {help ? (
                <span title={help} className="text-muted-foreground/70 cursor-help">
                  <HelpCircle className="h-3 w-3" />
                </span>
              ) : null}
            </div>

            {isSelect(input) ? (
              <Controller
                name={input.name}
                control={control}
                rules={{ required }}
                render={({ field }) => (
                  <DropdownField
                    input={input}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onResolvedOption={(option) =>
                      setSelectedOptions((prev) => {
                        const current = prev[input.name];
                        if (option && sameSelectedOption(current, option)) return prev;
                        if (!option && !current) return prev;
                        const next = { ...prev };
                        if (option) next[input.name] = option;
                        else delete next[input.name];
                        return next;
                      })
                    }
                    formValues={formValues}
                  />
                )}
              />
            ) : input.type === "number" ? (
              <Input
                type="number"
                placeholder={input.placeholder}
                {...register(input.name, { required })}
              />
            ) : input.type === "boolean" ? (
              <Controller
                name={input.name}
                control={control}
                render={({ field }) => (
                  <label className="inline-flex items-center gap-2 rounded-md border border-input bg-card px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={String(field.value) === "true"}
                      onChange={(event) => field.onChange(String(event.target.checked))}
                      className="h-4 w-4 accent-[hsl(var(--primary))]"
                    />
                    <span className="font-mono text-xs text-foreground">Enabled</span>
                  </label>
                )}
              />
            ) : (
              <Input
                type="text"
                placeholder={input.placeholder}
                {...register(input.name, { required })}
              />
            )}

            {help ? (
              <p className="text-[11px] font-mono text-muted-foreground/80 leading-snug">
                {help}
              </p>
            ) : null}
          </div>
        );
      })}

      <div className="flex items-center gap-3 pt-2">
        <Button
          type="submit"
          variant="charcoal"
          size="sm"
          disabled={status === "running" || formState.isSubmitting}
        >
          {status === "running" ? "Running…" : "Run Migration"}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => reset(defaultValues)}
        >
          Reset
        </Button>
      </div>
    </form>
  );
}

function enrichSelectedMetadata(
  payload: Record<string, unknown>,
  input: SkillInputSelect,
  rawValue: string,
  option: SearchableOption | undefined
) {
  const metadataId = option?.metadata_id || option?.metadata_item_id || option?.id;
  const metadataType =
    option?.metadata_type ||
    input.source_args?.metadata_type ||
    input.args?.metadata_type ||
    inferredMetadataType(input);
  const metadataName = option?.full_name || option?.name || rawValue;
  const isTargetInput = [
    "metadata_name",
    "metadataName",
    "class_name",
    "className",
    "object_name",
    "objectName",
    "apex_name",
    "apexName",
    "target",
  ].includes(input.name);

  if (!isTargetInput) return;
  if (metadataName && !payload.metadata_name) payload.metadata_name = metadataName;
  if (metadataType && !payload.metadata_type) payload.metadata_type = metadataType;
  if (metadataId) {
    payload.metadata_id = metadataId;
    payload.metadata_item_id = metadataId;
  }
  payload.selected_metadata = {
    id: metadataId,
    metadata_id: metadataId,
    metadata_item_id: metadataId,
    metadata_type: metadataType,
    full_name: metadataName,
    name: metadataName,
    file_name: option?.file_name,
  };
}

function inferredMetadataType(input: SkillInputSelect): string | undefined {
  if (input.source === "list_classes") return "ApexClass";
  if (input.source === "list_objects") return "CustomObject";
  if (input.source === "list_flows") return "Flow";
  if (input.source === "list_perm_sets") return "PermissionSet";
  return undefined;
}

function sameSelectedOption(left: SearchableOption | undefined, right: SearchableOption) {
  return (
    left?.value === right.value &&
    left?.metadata_id === right.metadata_id &&
    left?.metadata_item_id === right.metadata_item_id &&
    left?.id === right.id &&
    left?.metadata_type === right.metadata_type
  );
}
